import { Center, Title } from '@mantine/core';
import { LogLevel, Room, type VideoCodec, VideoPresets, videoCodecs } from 'livekit-client';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { ConferenceView } from '@/conference/conference-view';
import { useLowCPUOptimizer } from '@/conference/use-low-cpu-optimizer';
import { usePageLeaveDisconnect } from '@/conference/use-page-leave-disconnect';
import { useSetupE2EE } from '@/conference/use-setup-e2ee';
import { i18n } from '@/i18n';
import { notifyError } from '@/shared/notify';
import { isVideoCodec } from '@/shared/types';

export function CustomPage() {
    const { t } = useTranslation('settings');
    const [searchParams] = useSearchParams();
    const liveKitUrl = searchParams.get('liveKitUrl');
    const token = searchParams.get('token');
    const codec = searchParams.get('codec') ?? undefined;

    let problem: string | undefined;
    if (!liveKitUrl) {
        problem = t('custom.missingUrl');
    } else if (!token) {
        problem = t('custom.missingToken');
    } else if (codec !== undefined && !isVideoCodec(codec)) {
        problem = t('custom.unknownCodec', { codecs: videoCodecs.join(', ') });
    }

    if (problem || !liveKitUrl || !token) {
        return (
            <Center h="100%">
                <Title order={3}>{problem}</Title>
            </Center>
        );
    }

    return (
        <main className="meet-room" data-lk-theme="default">
            <CustomRoom
                codec={codec as VideoCodec | undefined}
                liveKitUrl={liveKitUrl}
                singlePeerConnection={searchParams.get('singlePC') === 'true'}
                token={token}
            />
        </main>
    );
}

function CustomRoom({
    liveKitUrl,
    token,
    codec,
    singlePeerConnection,
}: Readonly<{ liveKitUrl: string; token: string; codec: VideoCodec | undefined; singlePeerConnection: boolean }>) {
    const { worker, e2eePassphrase, keyProvider } = useSetupE2EE();
    const e2eeEnabled = Boolean(e2eePassphrase && worker);
    const [e2eeKeySet, setE2eeKeySet] = useState(false);
    const e2eeSetupComplete = !e2eeEnabled || e2eeKeySet;

    const room = useMemo(
        () =>
            new Room({
                publishDefaults: {
                    videoSimulcastLayers: [VideoPresets.h540, VideoPresets.h216],
                    red: !e2eeEnabled,
                    videoCodec: codec,
                },
                adaptiveStream: { pixelDensity: 'screen' },
                dynacast: true,
                e2ee: worker && e2eeEnabled ? { keyProvider, worker } : undefined,
                singlePeerConnection,
                disconnectOnPageLeave: false,
            }),
        [e2eeEnabled, codec, singlePeerConnection, keyProvider, worker]
    );

    useEffect(() => {
        if (!e2eeEnabled) return;
        keyProvider
            .setKey(e2eePassphrase)
            .then(() => room.setE2EEEnabled(true))
            .then(() => {
                setE2eeKeySet(true);
            })
            .catch((error: unknown) => {
                notifyError(i18n.t('settings:custom.e2eeFailed'), error);
            });
    }, [e2eeEnabled, e2eePassphrase, keyProvider, room]);

    useEffect(() => {
        if (!e2eeSetupComplete) return;
        room.connect(liveKitUrl, token, { autoSubscribe: true }).catch((error: unknown) => {
            notifyError(i18n.t('settings:custom.connectFailed'), error);
        });
        room.localParticipant.enableCameraAndMicrophone().catch((error: unknown) => {
            notifyError(i18n.t('settings:custom.mediaFailed'), error);
        });
    }, [room, liveKitUrl, token, e2eeSetupComplete]);

    usePageLeaveDisconnect(room);

    useLowCPUOptimizer(room);

    return <ConferenceView logLevel={LogLevel.debug} room={room} />;
}
